import { FC, useMemo, useState } from 'react';
import { Column } from 'react-table';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { useQuery, useQueryClient } from 'react-query';
import {
  FaCheck,
  FaCheckDouble,
  FaFile,
  FaFileSignature,
  FaInfo,
  FaPen,
  FaListAlt,
  FaEdit,
  FaTimes,
  FaExclamationCircle,
} from 'react-icons/fa';

import API from 'core/Api';
import { createErrorFromResponse } from 'core/Error';

import { useModal } from 'store/modal';
import { PermissionKeys } from 'store/types';
import { useLanguageContext } from 'store/language';
import { useActiveClientContext } from 'store/active-client';
import { usePermissionsContext } from 'store/permissions';

import { formatDate } from 'utils/helpers/formatters';
import { isIntigerOrFloat } from 'utils/helpers/leaves';
import { buildClientEntity } from 'utils/helpers/client';
import useWindowWidth from 'utils/helpers/useWindowWidth';

import { multiSignDocAsEmployer, signDocAsEmployer } from 'routes/client/documents/helpers';

import Button from 'components/Button';
import FetchTable from 'components/table/FetchTable';
import TooltipWrapper from 'components/TooltipWrapper';
import { IАttachment } from 'components/Attachment';
import { ITableFilter } from 'components/table/types';
import { buildFilters } from 'components/table/utils/filtersConfig';

import { ISelectOptionProps } from 'components/form/types';
import BadgeComponent from 'components/Badge';
import handlePreviewDoc from 'utils/helpers/handlePreviewDoc';
import { getSubdomain } from 'utils/helpers/routing';
import Spinner from 'components/Spinner';
import RejectLeave from '../components/RejectLeave';
import EmployerRejectLeave from '../components/EmployerRejectLeave';
import InnerWrapper from '../InnerWrapper';
import LeaveInterruptionInfoButton from '../components/LeaveInterruptionInfoButton';
import { ILeave } from '../types';

interface IConfirmButtonProps {
  employeeRejectionRequestId: number;
  documentHolderId: number;
}

interface IconBackgroundProps {
  color?: string;
}

const Screen: FC = () => {
  const { t } = useTranslation();
  const { client } = useActiveClientContext();
  const { language } = useLanguageContext();
  const history = useHistory();
  const { showModal, hideModal } = useModal();
  const { checkPermission } = usePermissionsContext();
  const queryClient = useQueryClient();
  const isOperator = getSubdomain() === 'operators';

  const [index, setIndex] = useState<number | null>(null);
  const [isPreviewing, setIsPreviewing] = useState(false);
  const [isOrderPreviewing, setIsOrderPreviewing] = useState(false);
  const [isApproving, setIsAproving] = useState(false);
  const [isSigning, setIsSigning] = useState(false);
  const [isApprovingAndSigning, setIsApprovingAndSigning] = useState(false);
  const [customSelectableRowProps, setCustomSelectableRowProps] = useState<any[]>([]);
  const [multiActionActive, setMultiActionActive] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [unprocessedLeaves, setUnprocessedLeaves] = useState<{
    count: number;
    min_date: string | null;
    max_date: string | null;
  } | null>(null);

  const windowWidth = useWindowWidth();
  const isMobile = windowWidth < 768;

  const formatDaysCount = (daysCount: string): string => {
    const parsed = parseFloat(daysCount);
    return parsed % 1 === 0 ? parsed.toFixed(0) : parsed.toFixed(1);
  };

  const approveLeave = (leaveId: number) => {
    return API.post(`leaves/${leaveId}/approve`)
      .then(res => res.data)
      .then(() => {
        queryClient.resetQueries(buildClientEntity('leaves', client));
        showModal({
          type: 'success',
          heading: t('common.success'),
          content: t('leaves.successfullyApproved'),
        });
      })
      .catch(err =>
        showModal({
          type: 'error',
          content: createErrorFromResponse(err).message,
        })
      );
  };

  const approveInSequenceLeave = (leaveId: number) => {
    return API.post(`leaves/${leaveId}/approve-in-sequence`)
      .then(res => res.data)
      .then(() => {
        queryClient.resetQueries(buildClientEntity('leaves', client));
        showModal({
          type: 'success',
          heading: t('common.success'),
          content: t('leaves.successfullyApproved'),
        });
      })
      .catch(err =>
        showModal({
          type: 'error',
          content: createErrorFromResponse(err).message,
        })
      );
  };

  const renderBadge = (status: string) => {
    const variant: {
      [key: string]: 'primary' | 'secondary' | 'success' | 'warning' | 'danger' | 'info' | 'light' | 'dark';
    } = {
      waiting_action: 'primary',
      in_progress: 'warning',
      rejected: 'danger',
      employeeRejectInitiated: 'warning',
    };

    const statuses: { [key: string]: string } = {
      initiated: t('leaves.initiated'),
      pending: t('leaves.pending'),
      signing: t('leaves.signing'),
      confirmed: t('leaves.confirmed'),
      approved: t('leaves.approved'),
      rejected: t('leaves.rejected'),
      cancelled: t('leaves.cancelled'),
      employeeRejectInitiated: t('leaves.employeeRejectInitiated'),
      interrupted: t('leaves.interrupted'),
    };

    if (status === 'rejected') {
      return (
        <BadgeComponent
          variant={variant[status]}
          label={statuses[status]}
          normalBadgeStyles
        />
      );
    }

    if (status === 'employeeRejectInitiated') {
      return (
        <BadgeComponent
          variant={variant[status]}
          label={statuses[status]}
          normalBadgeStyles
        />
      );
    }

    return <span style={{ fontSize: '14px', fontWeight: 'normal' }}>{statuses[status]}</span>;
  };

  const signLeave = (leaveId: number, leaveDetails: ILeave) => {
    const {
      has_enough_leave_days: hasEnoughLeaveDays,
      type,
      sub_type: subType,
      days_remaining_for_user: remainingDays,
      leave_days: paidDaysCount,
    } = leaveDetails;

    const formattedPaidDaysCount = formatDaysCount(paidDaysCount);

    if (!hasEnoughLeaveDays && type === 'paid' && subType === 'paid_ordinary') {
      showModal({
        type: 'confirm',
        heading: t('leaves.confirmNotEnoughDaysHeading'),
        content: t('leaves.confirmNotEnoughDaysMsg', {
          paidDaysCount: formattedPaidDaysCount,
          remainingDays,
        }),
        onConfirm: () => {
          setIsSigning(true);
          API.get(`leaves/${leaveId}/documents-holders`)
            .then(res => res.data)
            .then(({ request_document_holder_id: reqDocHolderId, order_document_holder_id: orderDocHolderId }) => {
              multiSignDocAsEmployer(
                [reqDocHolderId, orderDocHolderId],
                showModal,
                () => queryClient.resetQueries(buildClientEntity('leaves', client)),
                '',
                t
              ).then(() => {
                setIndex(null);
                setIsSigning(false);
              });
            })
            .catch(err =>
              showModal({
                type: 'error',
                content: createErrorFromResponse(err).message,
              })
            );
        },
      });
      return;
    }

    setIsSigning(true);
    API.get(`leaves/${leaveId}/documents-holders`)
      .then(res => res.data)
      .then(({ request_document_holder_id: reqDocHolderId, order_document_holder_id: orderDocHolderId }) => {
        multiSignDocAsEmployer(
          [reqDocHolderId, orderDocHolderId],
          showModal,
          () => queryClient.resetQueries(buildClientEntity('leaves', client)),
          '',
          t
        ).then(() => {
          setIndex(null);
          setIsSigning(false);
        });
      })
      .catch(err =>
        showModal({
          type: 'error',
          content: createErrorFromResponse(err).message,
        })
      );
  };

  const ConfirmEmployeeRejectRequestButton: FC<IConfirmButtonProps> = ({
    employeeRejectionRequestId,
    documentHolderId,
  }: IConfirmButtonProps) => {
    const [isLoadingConfirmReject, setIsLoadingConfirmReject] = useState(false);
    return (
      <Button
        key={5}
        type="button"
        btnType="info"
        loading={isLoadingConfirmReject}
        onClick={() => {
          if (isLoadingConfirmReject) {
            return;
          }

          setIsLoadingConfirmReject(true);

          API.post(`documents/${employeeRejectionRequestId}/approve`)
            .then(res => {
              signDocAsEmployer(
                employeeRejectionRequestId.toString(),
                documentHolderId.toString(),
                showModal,
                hideModal,
                () => queryClient.invalidateQueries(buildClientEntity('leaves', client)),
                'documents.table.approvedAndSignedDocSuccessMsg',
                t
              );
            })
            .catch(err => {
              signDocAsEmployer(
                employeeRejectionRequestId.toString(),
                documentHolderId.toString(),
                showModal,
                hideModal,
                () => queryClient.invalidateQueries(buildClientEntity('leaves', client)),
                'documents.table.approvedAndSignedDocSuccessMsg',
                t
              );
            });
        }}
      >
        {t('leaves.confirmEmployeeRejection')}
      </Button>
    );
  };

  const approveAndSignLeave = (leaveId: number) => {
    return API.post(`leaves/${leaveId}/approve`)
      .then(res => res.data)
      .then(({ request_document_holder_id: reqDocHolderId, order_document_holder_id: orderDocHolderId }) => {
        multiSignDocAsEmployer(
          [reqDocHolderId, orderDocHolderId],
          showModal,
          () => queryClient.resetQueries(buildClientEntity('leaves', client)),
          '',
          t
        );
      })
      .catch(err =>
        showModal({
          type: 'error',
          content: createErrorFromResponse(err).message,
        })
      );
  };

  const handleAttachmentClick = (id: number) => {
    return API.get(`leaves/attachment/${id}/download`)
      .then(res => {
        window.open(res.data, '_blank');
      })
      .catch(res => {
        const error = createErrorFromResponse(res);
        showModal({
          type: 'error',
          content: error.message,
        });
      });
  };

  const handleAttachmentsModal = (attachments: IАttachment[], commentText: string | undefined) => {
    const links = attachments.map(attachment => {
      return {
        id: attachment.id,
        fileName: `${attachment.file_name}.${attachment.extension}`,
      };
    });

    showModal({
      type: 'list',
      heading: t('leaves.attachmentsList'),
      onLinkClick: handleAttachmentClick,
      links,
      content: commentText ? (
        <span>
          <b style={{ fontSize: '20px' }}>Коментар от служител:</b> {commentText}
        </span>
      ) : undefined,
    });
  };

  const handleCommentsModal = (comment: string) => {
    showModal({
      type: 'info',
      heading: t('leaves.comment'),
      onLinkClick: handleAttachmentClick,
      content: comment,
    });
  };

  const formatUserAction = (actionArray: { user_name: string; timestamp: string }[], actionType: string) => {
    return actionArray.map(action => (
      <div key={`${actionType}-${action.timestamp}`}>
        {actionType} {t('leaves.from')} {action.user_name} {t('leaves.on')}{' '}
        {formatDate(new Date(action.timestamp), 'dd.mm.Y hh:ii')}
        {t('leaves.hoursSmall')}
      </div>
    ));
  };

  const getTooltipContent = (leave: ILeave) => {
    const shouldRenderDetails = leave.approved_by || leave.rejected_by || leave.signed_by;

    if (!shouldRenderDetails) {
      return (
        <div>
          {t('leaves.tooltip.id', { id: leave.id })}
          {leave?.substitute_employee_name ? (
            <div>
              {t('leaves.substituteEmployeeName')}: {leave.substitute_employee_name}
            </div>
          ) : null}
        </div>
      );
    }

    return (
      <div>
        <b>{t('leaves.tooltip.id', { id: leave.id })}</b>

        {leave?.substitute_employee_name ? (
          <div>
            {t('leaves.substituteEmployeeName')}: {leave.substitute_employee_name}
          </div>
        ) : null}

        {leave.approved_by?.length > 0 && formatUserAction(leave.approved_by, t('leaves.actions.approved'))}

        {leave.rejected_by?.length > 0 && formatUserAction(leave.rejected_by, t('leaves.actions.rejected'))}

        {leave.signed_by?.length > 0 && formatUserAction(leave.signed_by, t('leaves.actions.signed'))}
      </div>
    );
  };

  const renderActions = (
    buttons: {
      type: string;
      text: string;
      action_link: string;
      doc_holder_id?: number;
      disabled?: boolean;
      disabled_reason?: string;
    }[],
    leaveId: number,
    requestDocId: number,
    orderDocId: number,
    employeeRejectionRequestId: number,
    attachments: IАttachment[],
    commentText: string,
    leaveDetails: ILeave
  ) => {
    const actions =
      buttons?.map((button, i) => {
        const {
          type,
          text,
          action_link: actionLink,
          doc_holder_id: documentHolderId,
          disabled,
          disabled_reason: disabledReason,
        } = button;

        if (type === 'approve')
          return (
            <div
              key={i}
              className="d-inline-block ml-2"
            >
              <TooltipWrapper
                id="tooltip-approve"
                overlay={disabled && disabledReason ? disabledReason : text}
              >
                <Button
                  key={leaveId}
                  type="button"
                  btnType="success"
                  size="sm"
                  disabled={disabled || (isApproving && leaveId === index)}
                  ariaLabel="Approve document"
                  onClick={() => {
                    if (disabled) return;
                    setIndex(leaveId);
                    setIsAproving(true);
                    approveLeave(leaveId).then(() => {
                      setIndex(null);
                      setIsAproving(false);
                    });
                  }}
                >
                  {!(isApproving && leaveId === index) && <FaCheck size={14} />}

                  {isApproving && leaveId === index && <Spinner size={14} />}
                </Button>
              </TooltipWrapper>
            </div>
          );
        if (type === 'approve_in_sequence')
          return (
            <div
              key={i}
              className="d-inline-block ml-2"
            >
              <TooltipWrapper
                id="tooltip-approve-in-sequence"
                overlay={disabled && disabledReason ? disabledReason : text}
              >
                <Button
                  key={leaveId}
                  type="button"
                  btnType="secondary"
                  size="sm"
                  disabled={disabled || (isApproving && leaveId === index)}
                  ariaLabel="Approve in sequence document"
                  onClick={() => {
                    if (disabled) return;
                    setIndex(leaveId);
                    setIsAproving(true);
                    approveInSequenceLeave(leaveId).then(() => {
                      setIndex(null);
                      setIsAproving(false);
                    });
                  }}
                >
                  {!(isApproving && leaveId === index) && <FaCheckDouble size={14} />}

                  {isApproving && leaveId === index && <Spinner size={14} />}
                </Button>
              </TooltipWrapper>
            </div>
          );
        if (type === 'employeer_deny_reject')
          return (
            <div className="d-inline-block ml-2">
              <TooltipWrapper
                id="tooltip-reject"
                overlay={text}
              >
                <Button
                  type="button"
                  btnType="danger"
                  size="sm"
                  loading={isLoading && leaveId === index}
                  onClick={() => {
                    setIndex(leaveId);
                    setIsLoading(true);
                    API.patch(actionLink)
                      .then(res => {
                        setIsLoading(false);
                        setIndex(null);
                        queryClient.resetQueries(buildClientEntity('leaves', client));
                      })
                      .catch(err =>
                        showModal({
                          type: 'error',
                          content: createErrorFromResponse(err).message,
                        })
                      );
                  }}
                >
                  <FaTimes size={14} />
                </Button>
              </TooltipWrapper>
            </div>
          );
        if (type === 'employer_reject')
          return (
            <EmployerRejectLeave
              leaveId={leaveId}
              buttonText={text}
              handlePreview={() => handlePreviewDoc({ showModal, id: orderDocId })}
              invalidate={() => queryClient.invalidateQueries(buildClientEntity('leaves', client))}
            />
          );
        if (type === 'rejection_request_preview') {
          return (
            <div
              key={i}
              className="d-inline-block ml-2"
            >
              <TooltipWrapper
                id="tooltip-approve"
                overlay={text}
              >
                <Button
                  key={leaveId}
                  type="button"
                  btnType="danger"
                  size="sm"
                  disabled={isPreviewing && leaveId === index}
                  onClick={() => {
                    setIndex(leaveId);
                    handlePreviewDoc({ showModal, id: employeeRejectionRequestId }).then(() => {
                      setIndex(null);
                    });
                  }}
                >
                  {!(isPreviewing && leaveId === index) && <FaFile size={14} />}

                  {isPreviewing && leaveId === index && <Spinner size={14} />}
                </Button>
              </TooltipWrapper>
            </div>
          );
        }
        if (type === 'sign')
          return (
            <div
              key={i}
              className="d-inline-block ml-2"
            >
              <TooltipWrapper
                id="tooltip-approve"
                overlay={disabled && disabledReason ? disabledReason : text}
              >
                <Button
                  key={leaveId}
                  type="button"
                  btnType="info"
                  size="sm"
                  disabled={disabled || (isSigning && leaveId === index)}
                  onClick={() => {
                    if (disabled) return;
                    setIndex(leaveId);
                    signLeave(leaveId, leaveDetails);
                  }}
                >
                  {!(isSigning && leaveId === index) && <FaPen size={14} />}
                  {isSigning && leaveId === index && <Spinner size={14} />}
                </Button>
              </TooltipWrapper>
            </div>
          );
        if (type === 'approve_and_sign')
          return (
            <div
              key={i}
              className="d-inline-block ml-2"
            >
              <TooltipWrapper
                id="tooltip-approve"
                overlay={disabled && disabledReason ? disabledReason : text}
              >
                <Button
                  key={leaveId}
                  type="button"
                  btnType="info"
                  size="sm"
                  disabled={disabled || (isApprovingAndSigning && leaveId === index)}
                  loading={isApprovingAndSigning && leaveId === index}
                  ariaLabel="Approve and Sign"
                  onClick={() => {
                    if (disabled) return;
                    setIndex(leaveId);
                    setIsApprovingAndSigning(true);
                    approveAndSignLeave(leaveId).then(() => {
                      setIndex(null);
                      setIsApprovingAndSigning(false);
                    });
                  }}
                >
                  <FaCheck size={14} />
                </Button>
              </TooltipWrapper>
            </div>
          );
        if (type === 'reject')
          return (
            <RejectLeave
              key={leaveId}
              leaveId={leaveId}
              buttonText={text}
              handlePreview={() => handlePreviewDoc({ showModal, id: requestDocId })}
              invalidate={() => queryClient.invalidateQueries(buildClientEntity('leaves', client))}
            />
          );

        if (type === 'employeer_confirm_reject' && documentHolderId) {
          return (
            <div
              key={i}
              className="d-inline-block ml-2"
            >
              <TooltipWrapper
                id="tooltip-approve"
                overlay={text}
              >
                <Button
                  key={leaveId}
                  type="button"
                  btnType="info"
                  onClick={() => {
                    showModal({
                      type: 'form',
                      heading: t('documents.table.confirmDocHeading'),
                      content: (
                        <>
                          <p>{t('leaves.employer.confirmLeaveRejectRequestMsg')}</p>
                          <hr />
                          <ConfirmEmployeeRejectRequestButton
                            employeeRejectionRequestId={employeeRejectionRequestId}
                            documentHolderId={documentHolderId}
                          />
                        </>
                      ),
                    });
                  }}
                  size="sm"
                >
                  <FaCheck size={14} />
                </Button>
              </TooltipWrapper>
            </div>
          );
        }
        if (type === 'request_preview') {
          return (
            <div
              key={i}
              className="d-inline-block ml-2"
            >
              <TooltipWrapper
                id="tooltip-approve"
                overlay={text}
              >
                <Button
                  key={leaveId}
                  type="button"
                  btnType="info"
                  size="sm"
                  disabled={isPreviewing && leaveId === index}
                  onClick={() => {
                    setIndex(leaveId);
                    setIsPreviewing(true);
                    handlePreviewDoc({ showModal, id: requestDocId }).then(() => {
                      setIndex(null);
                      setIsPreviewing(false);
                    });
                  }}
                >
                  {!(isPreviewing && leaveId === index) && <FaFile size={14} />}

                  {isPreviewing && leaveId === index && <Spinner size={14} />}
                </Button>
              </TooltipWrapper>
            </div>
          );
        }
        if (type === 'order_preview') {
          return (
            <div
              key={i}
              className="d-inline-block ml-2"
            >
              <TooltipWrapper
                id="tooltip-order-preview"
                overlay={text}
              >
                <Button
                  key={leaveId}
                  type="button"
                  btnType="primary"
                  size="sm"
                  disabled={isOrderPreviewing && leaveId === index}
                  onClick={() => {
                    setIndex(leaveId);
                    setIsOrderPreviewing(true);
                    handlePreviewDoc({ showModal, id: orderDocId }).then(() => {
                      setIndex(null);
                      setIsOrderPreviewing(false);
                    });
                  }}
                >
                  {!(isOrderPreviewing && leaveId === index) && <FaFileSignature size={14} />}

                  {isOrderPreviewing && leaveId === index && <Spinner size={14} />}
                </Button>
              </TooltipWrapper>
            </div>
          );
        }
        if (type === 'rejection_request_preview') {
          return (
            <div
              key={i}
              className="d-inline-block ml-2"
            >
              <TooltipWrapper
                id="tooltip-approve"
                overlay={text}
              >
                <Button
                  key={leaveId}
                  type="button"
                  btnType="danger"
                  size="sm"
                  disabled={isPreviewing && leaveId === index}
                  onClick={() => {
                    setIndex(leaveId);
                    handlePreviewDoc({ showModal, id: employeeRejectionRequestId }).then(() => {
                      setIndex(null);
                    });
                  }}
                >
                  {!(isPreviewing && leaveId === index) && <FaFile size={14} />}

                  {isPreviewing && leaveId === index && <Spinner size={14} />}
                </Button>
              </TooltipWrapper>
            </div>
          );
        }
        if (type === 'attachments') {
          return (
            <div
              key={i}
              className="d-inline-block ml-2"
            >
              <TooltipWrapper
                id="tooltip-attachments"
                overlay={text}
              >
                <Button
                  key={leaveId}
                  type="button"
                  btnType="info"
                  size="sm"
                  onClick={() => handleAttachmentsModal(attachments, commentText)}
                >
                  <FaListAlt size={14} />
                </Button>
              </TooltipWrapper>
            </div>
          );
        }
        if (type === 'comment') {
          return (
            <div
              key={leaveId}
              className="d-inline-block ml-2"
            >
              <TooltipWrapper
                id="tooltip-comment"
                overlay={
                  <>
                    {text}: <br /> {commentText}
                  </>
                }
              >
                <Button
                  type="button"
                  btnType="info"
                  size="sm"
                  onClick={() => handleCommentsModal(commentText)}
                >
                  <FaInfo size={14} />
                </Button>
              </TooltipWrapper>
            </div>
          );
        }

        if (type === 'edit') {
          return (
            <div
              key={i}
              className="d-inline-block ml-2"
            >
              <TooltipWrapper
                id="tooltip-edit"
                overlay={<>{text}</>}
              >
                <Button
                  key={leaveId}
                  type="button"
                  btnType="primary"
                  outlined
                  size="sm"
                  onClick={() => history.push(`/leaves/manually/edit/${leaveId}`)}
                >
                  <FaEdit size={14} />
                </Button>
              </TooltipWrapper>
            </div>
          );
        }

        return null;
      }) || [];

    const { interrupting_sick_leave: interruptingSickLeave, interrupted_leaves: interruptedLeaves } = leaveDetails;

    if (interruptingSickLeave || (interruptedLeaves && interruptedLeaves.length > 0)) {
      actions.unshift(
        <LeaveInterruptionInfoButton
          key={`${leaveId}-interruption-info`}
          leaveId={leaveId}
          leaveDetails={leaveDetails}
        />
      );
    }

    return <div className="d-flex justify-content-end">{actions}</div>;
  };

  const columns: Array<Column<ILeave>> = [
    {
      Header: 'ID',
      accessor: 'id',
      disableSortBy: true,
    },
    {
      Header: t('leaves.employer.name') || '',
      accessor: 'user_name',
      disableSortBy: true,
      width: '15%',
      Cell: ({ row }) => {
        const leave = row.original;

        return (
          <TooltipWrapper
            id={`tooltip-name-${leave.id}`}
            overlay={getTooltipContent(leave)}
          >
            <span>{row.original.user_name}</span>
          </TooltipWrapper>
        );
      },
    },
    {
      Header: t('documents.table.group') || '',
      accessor: 'group_names',
      disableSortBy: true,
      width: '15%',
      Cell: ({ row }) => {
        const fullGroups = row.original.group_names.join(', ');
        const groupsPreview = fullGroups.length > 20 ? `${fullGroups.substr(0, 20)}...` : fullGroups;
        const leave = row.original;

        return (
          <TooltipWrapper
            id={`tooltip-group-${leave.id}`}
            overlay={getTooltipContent(leave)}
          >
            <span>{groupsPreview}</span>
          </TooltipWrapper>
        );
      },
    },
    {
      Header: t('leaves.type') || '',
      accessor: 'type_label',
      disableSortBy: true,
      width: '15%',
      Cell: ({ row }) => {
        const leave = row.original;
        return (
          <TooltipWrapper
            id={`tooltip-type-${leave.id}`}
            overlay={getTooltipContent(leave)}
          >
            <span>{leave.type_label || ''}</span>
          </TooltipWrapper>
        );
      },
    },
    {
      Header: t('leaves.dateFrom') || '',
      accessor: 'date_from',
      width: windowWidth < 1600 ? '7rem' : '4rem',
      Cell: ({ cell }) => {
        const dateFrom = new Date(cell.row.original.date_from);
        const subtractedDaysByYears = cell.row.original.subtracted_days_by_years;

        // Filter out entries where days are 0
        const filteredEntries = subtractedDaysByYears
          ? Object.entries(subtractedDaysByYears).filter(([_, days]) => days !== 0)
          : [];

        const tooltipContent =
          filteredEntries.length > 0 ? (
            <div>
              {filteredEntries.map(([year, days]) => (
                <>
                  {t('leaves.fromYear')} {year} {t('leaves.yearShortOnlyBG')} {t('leaves.used')} {days}{' '}
                  {t('leaves.days')}
                  <br />
                </>
              ))}
            </div>
          ) : null;

        return tooltipContent ? (
          <TooltipWrapper
            id={`tooltip-date-from-${cell.row.id}`}
            overlay={tooltipContent}
          >
            <span>{formatDate(dateFrom, 'dd.mm.YYY') || ''}</span>
          </TooltipWrapper>
        ) : (
          <span>{formatDate(dateFrom, 'dd.mm.YYY') || ''}</span>
        );
      },
    },
    {
      Header: t('leaves.dateTo') || '',
      accessor: 'date_to',
      disableSortBy: true,
      width: windowWidth < 1600 ? '7rem' : '4rem',
      Cell: ({ cell }) => {
        const dateTo = new Date(cell.row.original.date_to);
        const subtractedDaysByYears = cell.row.original.subtracted_days_by_years;

        // Filter out entries where days are 0
        const filteredEntries = subtractedDaysByYears
          ? Object.entries(subtractedDaysByYears).filter(([_, days]) => days !== 0)
          : [];

        const tooltipContent =
          filteredEntries.length > 0 ? (
            <div>
              {filteredEntries.map(([year, days]) => (
                <>
                  {t('leaves.fromYear')} {year} {t('leaves.yearShortOnlyBG')} {t('leaves.used')} {days}{' '}
                  {t('leaves.days')}
                  <br />
                </>
              ))}
            </div>
          ) : null;

        return tooltipContent ? (
          <TooltipWrapper
            id={`tooltip-date-to-${cell.row.id}`}
            overlay={tooltipContent}
          >
            <span>{formatDate(dateTo, 'dd.mm.YYY') || ''}</span>
          </TooltipWrapper>
        ) : (
          <span>{formatDate(dateTo, 'dd.mm.YYY') || ''}</span>
        );
      },
    },
    {
      Header: t('leaves.numberOfDays') || '',
      accessor: 'leave_days',
      disableSortBy: true,
      width: windowWidth < 1600 ? '5rem' : '2rem',
      Cell: ({ cell }) => {
        const subtractedDaysByYears = cell.row.original.subtracted_days_by_years;
        const leaveDays = cell.row.original.leave_days;

        // Filter out entries where days are 0
        const filteredEntries = subtractedDaysByYears
          ? Object.entries(subtractedDaysByYears).filter(([_, days]) => days !== 0)
          : [];

        const tooltipContent =
          filteredEntries.length > 0 ? (
            <div>
              {filteredEntries.map(([year, days]) => (
                <>
                  {t('leaves.fromYear')} {year} {t('leaves.yearShortOnlyBG')} {t('leaves.used')} {days}{' '}
                  {t('leaves.days')}
                  <br />
                </>
              ))}
            </div>
          ) : null;

        return tooltipContent ? (
          <TooltipWrapper
            id={`tooltip-leave-days-${cell.row.id}`}
            overlay={tooltipContent}
          >
            <span>{isIntigerOrFloat(leaveDays) || ''}</span>
          </TooltipWrapper>
        ) : (
          <span>{isIntigerOrFloat(leaveDays) || ''}</span>
        );
      },
    },
    {
      Header: t('leaves.status') || '',
      accessor: 'status',
      disableSortBy: true,
      width: '7.5rem',
      Cell: ({ row }) => {
        const leave = row.original;
        return (
          <TooltipWrapper
            id={`tooltip-status-${leave.id}`}
            overlay={getTooltipContent(leave)}
          >
            <span>{renderBadge(leave.status)}</span>
          </TooltipWrapper>
        );
      },
    },
  ];

  const mobileColumns: Array<Column<ILeave>> = [
    {
      Header: 'ID',
      accessor: 'id',
      disableSortBy: true,
    },
    {
      Header: t('leaves.type') || '',
      accessor: 'type_label',
      disableSortBy: true,
      width: '35%',
      Cell: row => {
        return row.value || '';
      },
    },
    {
      Header: t('leaves.dateFromTo') || '',
      accessor: 'date_from_to',
      width: '35%',
      Cell: ({ cell }) => {
        return (
          <>{`${formatDate(new Date(cell.row.original.date_from), 'dd.mm.YYY')} - ${formatDate(
            new Date(cell.row.original.date_to),
            'dd.mm.YYY'
          )}`}</>
        );
      },
    },
    {
      Header: t('leaves.numberOfDays') || '',
      accessor: 'leave_days',
      disableSortBy: true,
      width: '30%',
      Cell: ({ cell }) => {
        return <>{isIntigerOrFloat(cell?.value) || ''}</>;
      },
    },
  ];

  if (!multiActionActive) {
    columns.push({
      Header: () => <div style={{ textAlign: 'right' }}>{t('table.actions')}</div>,
      accessor: 'actions',
      disableSortBy: true,
      width: '20%',
      Cell: ({ row }) => {
        const {
          buttons,
          id,
          request_document_id: requestDocId,
          order_document_id: orderDocId,
          reject_request_document_id: employeeRejectionRequestId,
          attachments,
          comment: commentText,
        } = row.original;
        return (
          <div style={{ textAlign: 'right' }}>
            {renderActions(
              buttons,
              id,
              requestDocId,
              orderDocId,
              employeeRejectionRequestId,
              attachments,
              commentText,
              row.original
            )}
          </div>
        );
      },
    });
  }

  const {
    isFetching,
    isLoading: isLoadingLeavesDropdowns,
    error: errorDropdowns,
    data: dropdowns,
  } = useQuery<{ [key: string]: ISelectOptionProps[] }, Error>(
    [buildClientEntity('leaves-dropdowns', client)],
    (): Promise<{ [key: string]: ISelectOptionProps[] }> => {
      return API.get('leaves/dropdown-options').then(res => {
        const filters = res.data && res.data[language || 'bg'];
        return {
          ...filters,
          status: filters.status.map((filter: ITableFilter) => {
            if (filter.value === 'pending' || filter.value === 'approved' || filter.value === 'employeeRejectInitiated')
              return {
                ...filter,
                selected: true,
              };

            return filter;
          }),
        };
      });
    },
    {
      cacheTime: 0,
    }
  );

  const tableFilters = useMemo(
    () => [
      buildFilters({
        name: 'leave_period_start',
        placeholder: t('leaves.dateFrom'),
        type: 'monthyearpickerstart',
      }),
      buildFilters({
        name: 'leave_period_end',
        placeholder: t('leaves.dateTo'),
        ariaLabel: 'Leave date end',
        type: 'monthyearpickerend',
        subType: 'leavesWaitingApproval',
        setLastMonth: true,
      }),
      buildFilters({
        name: 'status',
        placeholder: t('table.statusFilter'),
        options: dropdowns?.status,
        isMulti: true,
        showTotal: true,
        allowSelectAll: true,
      }),
      buildFilters({
        name: 'user',
        placeholder: t('documents.table.employee'),
        options: dropdowns?.user,
        isMulti: true,
        limitOptions: true,
        showTotal: true,
        allowSelectAll: true,
        size: 'large',
      }),
      buildFilters({
        name: 'group',
        placeholder: t('table.groupFilter'),
        options: dropdowns?.group,
        isMulti: true,
        showTotal: true,
        allowSelectAll: true,
        size: 'large',
      }),
      buildFilters({
        name: 'type',
        placeholder: t('table.typeFilter'),
        options: dropdowns?.type,
        isMulti: true,
        showTotal: true,
        allowSelectAll: true,
      }),
    ],
    [dropdowns]
  );

  const handleDataFetched = (data: any) => {
    if (data.unprocessedLeaves) {
      setUnprocessedLeaves(data.unprocessedLeaves);
    } else {
      setUnprocessedLeaves(null);
    }
  };

  return (
    <InnerWrapper
      canMultiSign={
        checkPermission(PermissionKeys.CAN_MULTI_APPROVE) ||
        checkPermission(PermissionKeys.CAN_MULTI_SIGN) ||
        isOperator
      }
      customSelectableProps={customSelectableRowProps}
      setCustomSelectableRowProps={setCustomSelectableRowProps}
      setMultiActionActive={setMultiActionActive}
      isLoading={isFetching || isLoadingLeavesDropdowns}
    >
      <FetchTable<ILeave>
        loading={isFetching || isLoadingLeavesDropdowns}
        entityName={buildClientEntity('leaves', client)}
        entityEndpoint="leaves/search"
        columns={isMobile ? mobileColumns : columns}
        hasPermission={[PermissionKeys.CAN_MANAGE_LEAVES]}
        filters={tableFilters}
        pageLimit={10}
        pagination
        hideSearch
        isSelectable
        useCustomSelectBtn
        isLeavesMobile
        disablePagination={multiActionActive}
        customSelectableRowProps={customSelectableRowProps}
        disableMultiSort
        useBackendSorting
        onDataFetched={handleDataFetched}
        unprocessedLeaves={unprocessedLeaves}
      />
    </InnerWrapper>
  );
};

export default Screen;
